import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { getPointsExperiences } from 'actions/pointsExperiences'
import Info from 'components/Info'
import { buildTranslate } from 'locales'
import { CampaignConfig } from 'models/Campaign'
import React, { useEffect, useMemo } from 'react'
import Markdown from 'react-markdown'
import { useDispatch, useSelector } from 'react-redux'
import { MultiUseRewardsSettings } from 'scenes/RedeemManager/Builder/FormModel'
import {
  calculateEstimatedMaxValue,
  formatCurrency,
  getCostToBusiness,
  getDiscount,
  getMultiUseSettings,
  getTemplateKeyForType,
} from 'scenes/RedeemManager/helpers'
import { selectExperience } from 'selectors/pointsExperience'
import { selectRedeemTemplate } from 'selectors/redeemTemplate'

type PropsT = {
  config: CampaignConfig
}

const detailT = buildTranslate('redeem_manager.detail.cards.reward_details')
const typeT = buildTranslate('redeem_manager.types')

const getNumberOfUsesT = (settings: MultiUseRewardsSettings): string => {
  if (settings.usageType === 'exactly_once')
    return detailT('exactly_once_description')

  switch (settings.limitType) {
    case 'set_number':
      return detailT('multiple_times_set_number_description', {
        count: settings.maxIssuancesPerWindow,
        days: settings.windowDuration,
      })
    default:
      return detailT('multiple_times_unlimited_description', {
        count: settings.maxIssuances,
      })
  }
}

const General: React.FC<PropsT> = (props: PropsT) => {
  const { config } = props
  const dispatch = useDispatch()
  const pointsExperience = useSelector(selectExperience)
  const redeemTemplate = useSelector(state =>
    selectRedeemTemplate(state, config.redeem_template_id)
  )
  const typeKey = getTemplateKeyForType(
    config.redeem_type,
    config.redeem_subtype,
    redeemTemplate?.discount_scope
  )

  const discount = config.redeem_discount?.toString()

  const multiUseSettings = getMultiUseSettings(
    redeemTemplate?.multi_use_settings
  )
  const isMultiUse = multiUseSettings?.usageType === 'multiple_times'

  useEffect(() => {
    dispatch(getPointsExperiences())
  }, [dispatch])

  const estimatedMaxValue = useMemo(() => {
    return calculateEstimatedMaxValue(multiUseSettings, discount)
  }, [multiUseSettings, discount])

  const isFreeItem =
    config.redeem_type === 'manual/item' ||
    (config.redeem_type === 'manual/fixed_price' &&
      !!redeemTemplate?.discount_scope?.startsWith('free_'))
  const isBonusPoints = config.redeem_type === 'points/static'
  const longDescription = config.redeem_long_description ? (
    <Markdown className="grey-70">{config.redeem_long_description}</Markdown>
  ) : (
    '-'
  )

  return (
    <>
      <Info
        className="mb-m"
        label={detailT('reward_type')}
        value={typeT(`${typeKey}.title`)}
      />
      <Switch condition={isBonusPoints}>
        <Info
          className="mb-m"
          label={'Number of points'}
          value={
            <>
              <img
                src={pointsExperience?.currency_primary_icon.default}
                height="16"
                alt="currency"
              />
              <Text.BodyText4 className="ml-xs">
                {redeemTemplate?.number_of_points}
              </Text.BodyText4>
            </>
          }
        />
      </Switch>
      <Switch condition={!!longDescription && !isBonusPoints}>
        <Info
          className="mb-m"
          label={detailT('long_description')}
          value={longDescription}
        />
      </Switch>
      <Info
        className="mb-m"
        label={detailT('discount')}
        value={getDiscount(config.redeem_type, config.redeem_discount)}
      />
      <Info
        className="mb-m"
        label={detailT('number_of_uses')}
        value={getNumberOfUsesT(multiUseSettings)}
      />
      <Switch condition={isFreeItem}>
        <Info
          className="mb-m"
          label={detailT('cost_to_business')}
          value={getCostToBusiness(config.redeem_type, config.redeem_discount)}
        />
      </Switch>
      <Switch condition={config.redeem_retail_value > 0}>
        <Info
          className="mb-m"
          label={detailT('retail_price')}
          value={formatCurrency(config.redeem_retail_value)}
        />
      </Switch>
      <Switch condition={isMultiUse}>
        <Info
          className="mb-m"
          label={detailT('max_redemption_cost')}
          value={formatCurrency(estimatedMaxValue)}
        />
      </Switch>
    </>
  )
}

export default General
